import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, Upload, GraduationCap, Briefcase, IndianRupee, FileText, User, AlertCircle, CheckCircle } from 'lucide-react';
import { submitJobApplication } from '../../services/api';
import './JobApplication.css';

const JobApplication = () => {
  const location = useLocation();
  const [selectedRole, setSelectedRole] = useState(location.state?.role || '');
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    address: '',
    currentCTC: '',
    expectedCTC: '',
    noticePeriod: '',
    coverLetter: '',
    portfolioUrl: '',
    linkedinUrl: '',
    githubUrl: ''
  });

  const [resumeFile, setResumeFile] = useState(null);
  const [educationFields, setEducationFields] = useState([{
    degree: '',
    institution: '',
    yearOfPassing: '',
    percentage: ''
  }]);

  const [experienceFields, setExperienceFields] = useState([{
    company: '',
    position: '',
    startDate: '',
    endDate: '',
    currentlyWorking: false,
    responsibilities: ''
  }]);

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);

  const roles = [
    { value: 'senior-react-developer', label: 'Senior React Developer' },
    { value: 'ai-ml-engineer', label: 'AI/ML Engineer' },
    { value: 'ux-ui-designer', label: 'UX/UI Designer' },
    { value: 'devops-engineer', label: 'DevOps Engineer' },
    { value: 'backend-developer', label: 'Backend Developer (Node.js/Python)' },
    { value: 'flutter-developer', label: 'Flutter Developer' },
    { value: 'qa-engineer', label: 'QA Engineer' },
    { value: 'project-manager', label: 'Project Manager' },
    { value: 'business-analyst', label: 'Business Analyst' },
    { value: 'intern-frontend', label: 'Frontend Development Intern' }
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!validTypes.includes(file.type)) {
        setError('Please upload PDF or DOC/DOCX file only');
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        setError('File size should be less than 5MB');
        return;
      }
      
      setResumeFile(file);
      setError('');
    }
  };

  const addEducationField = () => {
    setEducationFields([...educationFields, {
      degree: '',
      institution: '',
      yearOfPassing: '',
      percentage: ''
    }]);
  };

  const removeEducationField = (index) => {
    const newFields = educationFields.filter((_, i) => i !== index);
    setEducationFields(newFields);
  };

  const updateEducationField = (index, field, value) => {
    const newFields = [...educationFields];
    newFields[index][field] = value;
    setEducationFields(newFields);
  };

  const addExperienceField = () => {
    setExperienceFields([...experienceFields, {
      company: '',
      position: '',
      startDate: '',
      endDate: '',
      currentlyWorking: false,
      responsibilities: ''
    }]);
  };

  const removeExperienceField = (index) => {
    const newFields = experienceFields.filter((_, i) => i !== index);
    setExperienceFields(newFields);
  };

  const updateExperienceField = (index, field, value) => {
    const newFields = [...experienceFields];
    newFields[index][field] = value;
    setExperienceFields(newFields);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setUploadProgress(0);

    try {
      setUploadProgress(20);
      
      // Prepare form data to send to backend
      const formDataToSend = {
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        dateOfBirth: formData.dateOfBirth,
        address: formData.address,
        currentCTC: formData.currentCTC,
        expectedCTC: formData.expectedCTC,
        noticePeriod: formData.noticePeriod,
        coverLetter: formData.coverLetter,
        portfolioUrl: formData.portfolioUrl,
        linkedinUrl: formData.linkedinUrl,
        githubUrl: formData.githubUrl,
        position: roles.find(r => r.value === selectedRole)?.label || selectedRole
      };

      setUploadProgress(50);
      
      // Submit to backend
      await submitJobApplication(formDataToSend, resumeFile, educationFields, experienceFields);
      
      setUploadProgress(100);
      setSubmitted(true);
      
    } catch (err) {
      console.error('Error submitting application:', err);
      setError(err.message || 'Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="application-success">
        <div className="container">
          <div className="success-card">
            <div className="success-icon">✓</div>
            <h2>Application Submitted Successfully!</h2>
            <p>Thank you for applying to Sritha Solutions. Our HR team will review your application and get back to you within 5-7 business days.</p>
            <div className="success-details">
              <CheckCircle size={18} />
              <span>Application sent to HR team</span>
            </div>
            <div className="success-details">
              <CheckCircle size={18} />
              <span>Confirmation email sent to your inbox</span>
            </div>
            <Link to="/careers" className="btn btn-primary">
              Back to Careers
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="job-application">
      <div className="application-header">
        <div className="container">
          <Link to="/careers" className="back-link">
            <ArrowLeft size={20} /> Back to Careers
          </Link>
          <h1>Job Application</h1>
          <p>Please fill out the form below to apply for your desired position</p>
        </div>
      </div>

      <div className="application-form-container">
        <div className="container">
          <form onSubmit={handleSubmit} className="application-form">
            {error && (
              <div className="error-message">
                <AlertCircle size={20} />
                {error}
              </div>
            )}

            {loading && (
              <div className="progress-bar-container">
                <div className="progress-bar" style={{ width: `${uploadProgress}%` }}></div>
                <p>Submitting application... {uploadProgress}%</p>
              </div>
            )}

            {/* Role Selection */}
            <div className="form-section">
              <h2>
                <Briefcase size={24} />
                Position Details
              </h2>
              <div className="form-group">
                <label>Applying For *</label>
                <select 
                  value={selectedRole} 
                  onChange={(e) => setSelectedRole(e.target.value)}
                  required
                >
                  <option value="">Select a position</option>
                  {roles.map(role => (
                    <option key={role.value} value={role.value}>{role.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Personal Information */}
            <div className="form-section">
              <h2>
                <User size={24} />
                Personal Information
              </h2>
              <div className="form-row">
                <div className="form-group">
                  <label>Full Name *</label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    required
                    placeholder="Enter your full name"
                  />
                </div>
                <div className="form-group">
                  <label>Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="Enter your email"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="Enter your phone number"
                  />
                </div>
                <div className="form-group">
                  <label>Date of Birth</label>
                  <input
                    type="date"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Address</label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Enter your full address"
                ></textarea>
              </div>
            </div>

            {/* Education Details */}
            <div className="form-section">
              <h2>
                <GraduationCap size={24} />
                Education Details
              </h2>
              {educationFields.map((edu, index) => (
                <div key={index} className="education-entry">
                  <div className="entry-header">
                    <h3>Education {index + 1}</h3>
                    {educationFields.length > 1 && (
                      <button type="button" className="remove-btn" onClick={() => removeEducationField(index)}>
                        Remove
                      </button>
                    )}
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Degree/Course *</label>
                      <input
                        type="text"
                        value={edu.degree}
                        onChange={(e) => updateEducationField(index, 'degree', e.target.value)}
                        required
                        placeholder="e.g., B.Tech in Computer Science"
                      />
                    </div>
                    <div className="form-group">
                      <label>Institution *</label>
                      <input
                        type="text"
                        value={edu.institution}
                        onChange={(e) => updateEducationField(index, 'institution', e.target.value)}
                        required
                        placeholder="Name of college/university"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Year of Passing *</label>
                      <input
                        type="number"
                        value={edu.yearOfPassing}
                        onChange={(e) => updateEducationField(index, 'yearOfPassing', e.target.value)}
                        required
                        placeholder="YYYY"
                      />
                    </div>
                    <div className="form-group">
                      <label>Percentage/CGPA *</label>
                      <input
                        type="text"
                        value={edu.percentage}
                        onChange={(e) => updateEducationField(index, 'percentage', e.target.value)}
                        required
                        placeholder="e.g., 85% or 8.5 CGPA"
                      />
                    </div>
                  </div>
                </div>
              ))}
              <button type="button" className="add-btn" onClick={addEducationField}>
                + Add Another Education
              </button>
            </div>

            {/* Work Experience */}
            <div className="form-section">
              <h2>
                <Briefcase size={24} />
                Work Experience
              </h2>
              {experienceFields.map((exp, index) => (
                <div key={index} className="experience-entry">
                  <div className="entry-header">
                    <h3>Experience {index + 1}</h3>
                    {experienceFields.length > 1 && (
                      <button type="button" className="remove-btn" onClick={() => removeExperienceField(index)}>
                        Remove
                      </button>
                    )}
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Company Name</label>
                      <input
                        type="text"
                        value={exp.company}
                        onChange={(e) => updateExperienceField(index, 'company', e.target.value)}
                        placeholder="Company name"
                      />
                    </div>
                    <div className="form-group">
                      <label>Position/Title</label>
                      <input
                        type="text"
                        value={exp.position}
                        onChange={(e) => updateExperienceField(index, 'position', e.target.value)}
                        placeholder="Your job title"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Start Date</label>
                      <input
                        type="month"
                        value={exp.startDate}
                        onChange={(e) => updateExperienceField(index, 'startDate', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>End Date</label>
                      <input
                        type="month"
                        value={exp.endDate}
                        onChange={(e) => updateExperienceField(index, 'endDate', e.target.value)}
                        disabled={exp.currentlyWorking}
                      />
                    </div>
                  </div>
                  <div className="form-group checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={exp.currentlyWorking}
                        onChange={(e) => updateExperienceField(index, 'currentlyWorking', e.target.checked)}
                      />
                      I currently work here
                    </label>
                  </div>
                  <div className="form-group">
                    <label>Key Responsibilities</label>
                    <textarea
                      value={exp.responsibilities}
                      onChange={(e) => updateExperienceField(index, 'responsibilities', e.target.value)}
                      rows="3"
                      placeholder="Describe your key responsibilities and achievements"
                    ></textarea>
                  </div>
                </div>
              ))}
              <button type="button" className="add-btn" onClick={addExperienceField}>
                + Add Another Experience
              </button>
            </div>

            {/* Compensation Details */}
            <div className="form-section">
              <h2>
                <IndianRupee size={24} />
                Compensation Details
              </h2>
              <div className="form-row">
                <div className="form-group">
                  <label>Current CTC (Annual)</label>
                  <input
                    type="text"
                    name="currentCTC"
                    value={formData.currentCTC}
                    onChange={handleChange}
                    placeholder="e.g., ₹8,00,000"
                  />
                </div>
                <div className="form-group">
                  <label>Expected CTC (Annual) *</label>
                  <input
                    type="text"
                    name="expectedCTC"
                    value={formData.expectedCTC}
                    onChange={handleChange}
                    required
                    placeholder="e.g., ₹12,00,000"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Notice Period</label>
                <select
                  name="noticePeriod"
                  value={formData.noticePeriod}
                  onChange={handleChange}
                >
                  <option value="">Select notice period</option>
                  <option value="immediate">Immediate</option>
                  <option value="15-days">15 Days</option>
                  <option value="30-days">30 Days</option>
                  <option value="45-days">45 Days</option>
                  <option value="60-days">60 Days</option>
                  <option value="90-days">90 Days</option>
                </select>
              </div>
            </div>

            {/* Resume Upload */}
            <div className="form-section">
              <h2>
                <Upload size={24} />
                Resume Upload
              </h2>
              <div className="form-group">
                <label>Upload Resume * (PDF, DOC, DOCX - Max 5MB)</label>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={handleFileChange}
                  required
                />
                <small>Your resume will be attached to the email sent to our HR team</small>
                {resumeFile && (
                  <div className="file-info">
                    ✓ File selected: {resumeFile.name} ({(resumeFile.size / 1024).toFixed(2)} KB)
                  </div>
                )}
              </div>
            </div>

            {/* Additional Information */}
            <div className="form-section">
              <h2>
                <FileText size={24} />
                Additional Information
              </h2>
              <div className="form-group">
                <label>Cover Letter / Why do you want to join us?</label>
                <textarea
                  name="coverLetter"
                  value={formData.coverLetter}
                  onChange={handleChange}
                  rows="5"
                  placeholder="Tell us why you're interested in this role and what makes you a great fit..."
                ></textarea>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Portfolio/Website URL</label>
                  <input
                    type="url"
                    name="portfolioUrl"
                    value={formData.portfolioUrl}
                    onChange={handleChange}
                    placeholder="https://yourportfolio.com"
                  />
                </div>
                <div className="form-group">
                  <label>LinkedIn Profile</label>
                  <input
                    type="url"
                    name="linkedinUrl"
                    value={formData.linkedinUrl}
                    onChange={handleChange}
                    placeholder="https://linkedin.com/in/yourprofile"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>GitHub Profile</label>
                <input
                  type="url"
                  name="githubUrl"
                  value={formData.githubUrl}
                  onChange={handleChange}
                  placeholder="https://github.com/yourusername"
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="form-actions">
              <button type="submit" className="btn btn-primary submit-btn" disabled={loading}>
                {loading ? 'Submitting...' : 'Submit Application'}
              </button>
              <Link to="/careers" className="btn btn-outline">Cancel</Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default JobApplication;